import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import * as React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import classnames from 'classnames';
import { Notification } from '@thisisbud/bui-react-notification';
import { selectFromTheme } from '@thisisbud/sc-utils';
import _findIndexInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/find-index';
import _spliceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/splice';
import _setTimeout from '@babel/runtime-corejs3/core-js-stable/set-timeout';
import _bindInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/bind';
import _forEachInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/for-each';
import _Object$keys from '@babel/runtime-corejs3/core-js-stable/object/keys';
import 'core-js/modules/web.dom-collections.iterator.js';
import { v4 } from 'uuid';
import { jsx, jsxs } from 'react/jsx-runtime';

const defaultToastDuration = 6000; // 6 seconds

const defaultToastType = 'info';

var ToastNotificationsContext = React.createContext(undefined);

/**
 * Use a stack of toast notifications and the API to handle notifications.
 *
 * @returns The current stack of notifications and the API to handle notifications
 */
function useStack() {
  const [notifications, setNotifications] = React.useState([]);
  const timeouts = React.useRef({});
  const remove = React.useCallback(function (id) {
    setNotifications(function (value) {
      const index = _findIndexInstanceProperty(value).call(value, function (notification) {
        return notification.id === id;
      });

      // The notification does not exist and no changes are necessary
      if (index === -1) {
        return value;
      }

      // Remove the notification from the stack
      const updatedNotifications = [...value];
      _spliceInstanceProperty(updatedNotifications).call(updatedNotifications, index, 1);
      return updatedNotifications;
    });
    clearTimeout(timeouts.current[id]);
  }, []);
  const push = React.useCallback(function (notification) {
    const {
      duration = defaultToastDuration,
      label,
      message,
      type = defaultToastType
    } = notification;
    const id = v4();
    const toastOptions = {
      id,
      label,
      message,
      type
    };

    // Add the notification to the stack
    setNotifications(function (value) {
      return [...value, toastOptions];
    });

    // Schedule the removal of the notification
    timeouts.current[id] = _setTimeout(_bindInstanceProperty(remove).call(remove, undefined, id), duration);
    return id;
  }, []);
  React.useEffect(function () {
    return function () {
      var _context;
      // Clean up timeouts on unmount
      _forEachInstanceProperty(_context = _Object$keys(timeouts.current)).call(_context, function (id) {
        clearTimeout(timeouts.current[id]);
      });
    };
  }, []);
  const api = React.useMemo(function () {
    return {
      push,
      remove
    };
  }, []);
  return {
    api,
    notifications
  };
}

/**
 * Use the toast notifications API.
 *
 * @returns The API to use toast notifications
 * @public
 */
function useToastNotifications() {
  return React.useContext(ToastNotificationsContext); // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

let _ = t => t,
  _t;
const toastNotificationsClassName = 'bui-toast-notifications';
const toastItemClassName = `${toastNotificationsClassName}__item`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledRoot = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledRoot",
  componentId: "sc-b543sg-0"
})(_t || (_t = _`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  left: 0;
  top: ${0};
  width: 100%;
  height: 0px;
  padding: 0 ${0};
  overflow: visible;
  box-sizing: border-box;
  z-index: 1000;

  .${0} {
    width: auto;
    max-width: ${0};
    box-sizing: border-box;
  }

  .${0} + .${0} {
    margin-top: ${0};
  }
`), selectFromTheme('spacingSmallDefault'), selectFromTheme('spacingXSmallDefault'), toastItemClassName, "38.75rem", toastItemClassName, toastItemClassName, selectFromTheme('spacingXXXSmallDefault'));

/**
 * Display toast notifications on top of a page.
 * This components provides an API to push or remove notifications which should be consumed using the provided hook.
 *
 * @remarks Notifications should only be pushed in a browser context.
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import ToastNotifications, { useToastNotifications } from '@thisisbud/bui-react-toast-notifications'
 *
 * function Greeting() {
 *   const toastNotifications = useToastNotifications()
 *
 *   React.useEffect(function () {
 *     const notificationId = toastNotifications.push({
 *       label: 'Notice',
 *       message: 'Welcome to BUI',
 *     })
 *
 *     return function () {
 *       toastNotifications.remove(notificationId)
 *     }
 *   }, [])
 * }
 *
 * function Root() {
 *   return (
 *     <ToastNotifications>
 *       <Greeting/>
 *     </ToastNotifications>
 *   )
 * }
 * ```
 */
function ToastNotifications(props) {
  const {
    children,
    className,
    id
  } = props;
  const {
    api,
    notifications
  } = useStack();
  const portal = typeof document !== 'undefined' ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/jsx(StyledRoot, {
    "aria-live": "assertive",
    "aria-relevant": "additions",
    className: classnames(toastNotificationsClassName, className),
    id: id,
    children: _mapInstanceProperty(notifications).call(notifications, function (notification) {
      return /*#__PURE__*/jsx(Notification, {
        as: "output",
        className: toastItemClassName,
        id: `${id}-toast-${notification.id}`,
        label: notification.label,
        role: "alert",
        type: notification.type,
        children: notification.message
      }, notification.id);
    })
  }), document.body) : undefined;
  return /*#__PURE__*/jsxs(ToastNotificationsContext.Provider, {
    value: api,
    children: [children, portal]
  });
}
ToastNotifications.displayName = 'ToastNotifications';

export { ToastNotifications as default, useToastNotifications };
